<script>
import { ref, onMounted, computed } from 'vue';
import { DatePicker } from 'v-calendar';
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { success } from "@/use/toast/success";

import { fetchByType } from '@/use/repositories/feedbacks/fetchByType';
import { calculateDeliveryDate } from '@/use/repositories/clinic-deliveries/calculateDeliveryDate';
import { requestDeliveryDateChange } from '@/use/repositories/clinic-deliveries/requestDeliveryDateChange';
import moment from "moment";
import { error } from "@/use/toast/error";

import { dateFormatter } from '@/use/utilities/time/dateFormatter';
import Brand from "@/components/Brand";
export default {
  name: "IsoInChangedDate",
  inheritAttrs: false,
  props: [
    'test_work_queue_id',
    'test_work_queue_phase_id',
    'internal_phase_id',
    'test_id',
    'state_slug',
    'delivery_date',
    'prescription',
    'clinic_id',
  ],
  components: {
    DatePicker,
    Brand
  },
  setup(props) {
    const router = useRouter();
    const i18n = useI18n();
    const date = ref(null);
    const deliveryDate = ref(null);
    const feedbacks = ref();
    const feedbackId = ref(null);
    const explanation = ref(null);
    const isRequesting = ref(false);

    const isAllowedToSubmit = computed(() => deliveryDate.value && feedbackId.value);

    const calculateDeliveryDateHandler = () => {
      calculateDeliveryDate({
        clinicId: props.clinic_id,
        appointmentDate: moment(date.value).format("YYYY-MM-DD HH:mm:ss"),
      }).then(async (response) => {
        if (response.date && response.time) {
          const backendDate = `${response.date} ${response.time}`;
          deliveryDate.value = moment(backendDate).toDate();
        } else if (response.delivery_date) {
          deliveryDate.value = moment(response.delivery_date).toDate();
        } else {
          console.error('Unexpected response format:', response);
        }
        
        explanation.value = response.explanation || null;
      });
    }

    const requestDeliveryDateChangeHandler = async () => {
      if (!deliveryDate.value || !feedbackId.value) {
        error(i18n.t("Compila tutti i campi obbligatori"));
        return;
      }

      isRequesting.value = true;

      try {
        await requestDeliveryDateChange({
          testWorkQueueId: props.test_work_queue_id,
          deliveryDate: dateFormatter(deliveryDate.value, { onlyDate: false, onlyTime: false, dash: true, iso: false }),
          feedbackId: feedbackId.value,
          appointmentDate: date.value ? moment(date.value).format("YYYY-MM-DD HH:mm:ss") : null,
          printLabel: false,
          printerDepartment: null,
        });

        success(i18n.t("Richiesta di cambio data inviata al manager con successo!"));
        setTimeout(() => {
          router.go(-1);
        }, 2000);
      } catch (err) {
        console.error('Errore nella richiesta di cambio data:', err);
        error(i18n.t("Errore nell'invio della richiesta. Riprova."));
      } finally {
        isRequesting.value = false;
      }
    }

    const getFeedbackId = (value) => {
      feedbackId.value = value.value;
    };

    onMounted(() => {
      fetchByType('delivery-date-change', {
        action: 'with_feedbacks',
        internal_phase_id: props.internal_phase_id,
        state_slug: 'in-the-works',
      }).then((response) => {
        feedbacks.value = response.feedbacks;
      });
    });

    return {
      date,
      feedbacks,
      feedbackId,
      requestDeliveryDateChangeHandler,
      getFeedbackId,
      isAllowedToSubmit,
      deliveryDate,
      calculateDeliveryDateHandler,
      explanation,
      isRequesting,
    }
  }
}
</script>

<template>
  <div class="d-flex vw-100 vh-100 justify-content-center align-items-center">
    <div class="col-sm-12 col-md-10 col-lg-8 col-xl-7 col-xxl-6">
      <Brand :width="120" :height="120" />
      <div class="card rounded-custom shadow border-0 p-4 flex-column justify-content-center">
        <h4 class="mb-4 fw-normal text-center">{{ `Seleziona la data e l'orario dell'appuntamento per la prescrizione
          n. ${$props.prescription}` }}</h4>
        <DatePicker class="m-auto mb-3" v-model="date" mode="dateTime" is24hr locale="it" />
        <div class="d-grid gap-2 col-6 mx-auto" v-if="date">
          <button type="button" class="btn btn-violet text-uppercase me-3" @click="calculateDeliveryDateHandler()">Calcola data etichetta</button>
        </div>
        <div v-if="explanation" class="alert alert-info mx-auto col-12 mt-3">
          {{ explanation }}
        </div>
        <div class="p-5">
          <div class="mb-3">
            <label class="form-label">Data etichetta</label>
            <DatePicker v-model="deliveryDate" :update-on-input="false" mode="dateTime" is24hr locale="it">
              <template v-slot="{ inputValue, inputEvents }">
                <input class="bg-white border px-2 py-1 rounded" :value="inputValue" v-on="inputEvents" />
              </template>
            </DatePicker>
          </div>
          <label class="form-label">Seleziona la motivazione</label>
          <select class="form-control mb-3" v-model="feedbackId">
            <option :value="null">... </option>
            <option v-for="(feedback, index) in feedbacks" :key="index" :value="feedback.id">
              {{ feedback.description }}
            </option>
          </select>
        </div>
        <button type="button" :disabled="!isAllowedToSubmit || isRequesting" class="btn btn-violet text-uppercase mt-4 m-auto"
          @click="requestDeliveryDateChangeHandler">
          <span v-if="isRequesting">{{ $t('Invio in corso...') }}</span>
          <span v-else>{{ $t('Richiedi cambio data') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
